"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const index_1 = require("../index");
const TimePickerPeriodToggle_1 = __importDefault(require("./TimePickerPeriodToggle"));
const ArrowDropDown_1 = __importDefault(require("@mui/icons-material/ArrowDropDown"));
const TimePickerOptions_1 = __importDefault(require("./TimePickerOptions"));
const utils_1 = require("./utils");
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const useOptionsPicker_1 = __importDefault(require("./useOptionsPicker"));
const dateTime_1 = require("../../utils/dateTime");
const TimePickerPeriodSelect_1 = __importDefault(require("./TimePickerPeriodSelect"));
const MAX_MILITARY = 23;
const MILITARY_HOUR_FORMAT = 'HH:mm';
const MAX_AM_PM = 12;
const AM_PM_HOUR_FORMAT = 'hh:mm';
const AM_PM_FULL_FORMAT = 'h:mm A';
const AM_PM_DISPLAY_FORMAT = 'h:mma';
const calculateFixedOptionsStyle = (el) => {
    const input = el.querySelector('input');
    if (!input) {
        return {};
    }
    const rect = input.getBoundingClientRect();
    const top = rect.top + rect.height;
    const left = rect.left;
    const width = rect.width;
    return {
        position: 'fixed',
        marginTop: '8px',
        top: `${top}px`,
        left: `${left}px`,
        right: 'auto',
        width: `${width}px`,
    };
};
const useFixedOptions = (el, skip = false) => {
    const elementRef = (0, react_1.useRef)(null);
    (0, react_1.useLayoutEffect)(() => {
        const handleScroll = () => {
            if (el && !skip && elementRef.current) {
                const styles = calculateFixedOptionsStyle(el);
                Object.keys(styles).forEach((key) => {
                    // @ts-ignore
                    elementRef.current.style[key] = styles[key];
                });
            }
        };
        handleScroll();
        document?.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            document?.removeEventListener('scroll', handleScroll);
        };
    }, [el, skip]);
    return { elementRef };
};
const TimePicker = ({ value, onChange, military = false, isPeriodSelect = false, meta, timezone, date, placeholder, optionsMode = 'relative', ...rest }) => {
    const field = (0, react_1.useRef)();
    const dateMoment = (0, moment_timezone_1.default)(date);
    const timeValue = (0, moment_timezone_1.default)(value).format(military ? MILITARY_HOUR_FORMAT : AM_PM_HOUR_FORMAT);
    const periodValue = (0, moment_timezone_1.default)(value).format('A');
    const picker = (0, useOptionsPicker_1.default)({
        onOpen: rest.onFocus,
        onClose: rest.onBlur,
    });
    const [inputValue, setInputValue] = (0, react_1.useState)(value ? timeValue : '');
    const [period, setPeriod] = (0, react_1.useState)(value ? periodValue : 'AM');
    const options = (0, react_1.useMemo)(() => (0, utils_1.filter)(`${(inputValue || '').replace(/_/g, '0')}`, period), [inputValue, period]);
    const fixedOptions = useFixedOptions(field.current, optionsMode === 'relative' || !picker.isOpen || military);
    const getPeriodType = () => {
        if (military) {
            return (value &&
                !meta?.error &&
                !rest.helperText && (react_1.default.createElement(index_1.FormHelperText, { variant: "outlined" }, (0, moment_timezone_1.default)(value).format(AM_PM_DISPLAY_FORMAT))));
        }
        else if (isPeriodSelect) {
            return (react_1.default.createElement(TimePickerPeriodSelect_1.default, { value: period, onFocus: rest.onFocus, onBlur: rest.onBlur, onChange: (period) => {
                    handleChange(inputValue, period);
                    setPeriod(period);
                } }));
        }
        else {
            return (react_1.default.createElement(TimePickerPeriodToggle_1.default, { value: period, onFocus: rest.onFocus, onBlur: rest.onBlur, onChange: (period) => {
                    handleChange(inputValue, period);
                    setPeriod(period);
                } }));
        }
    };
    const handleChange = (newValue, newPeriod) => {
        const timeValue = newValue || '';
        // For stopgap IMask lazy
        if (timeValue.includes('h') || timeValue.includes('m') || timeValue.length < 5) {
            onChange?.(undefined);
            setInputValue(timeValue);
            return;
        }
        const [h, m] = timeValue.split(':');
        const max = military ? MAX_AM_PM : MAX_MILITARY;
        if (+h > max && Number(m) > 59) {
            onChange?.(undefined);
            return;
        }
        const pickerValue = military
            ? (0, moment_timezone_1.default)(newValue, MILITARY_HOUR_FORMAT).tz(timezone, true)
            : (0, moment_timezone_1.default)(`${newValue} ${newPeriod}`, AM_PM_FULL_FORMAT).tz(timezone, true);
        if (pickerValue.isValid() && onChange) {
            const newMoment = (0, dateTime_1.setDateToTimeMoment)(dateMoment, pickerValue);
            if (!newMoment.isSame(value)) {
                onChange?.(newMoment);
            }
        }
    };
    (0, react_1.useEffect)(() => {
        setInputValue((prevInputValue) => {
            const timeValue = (0, moment_timezone_1.default)(value).format(military ? MILITARY_HOUR_FORMAT : AM_PM_HOUR_FORMAT);
            if (value && timeValue !== 'Invalid date' && timeValue !== inputValue) {
                handleChange(timeValue, periodValue);
                return timeValue;
            }
            return prevInputValue;
        });
    }, [value]);
    (0, react_1.useEffect)(() => {
        setPeriod((prevPeriodValue) => {
            const periodValue = (0, moment_timezone_1.default)(value).format('A');
            if (value && periodValue !== 'Invalid date' && periodValue !== prevPeriodValue) {
                handleChange(timeValue, periodValue);
                return periodValue;
            }
            return prevPeriodValue;
        });
    }, [value]);
    (0, react_1.useEffect)(() => {
        const valueMoment = (0, moment_timezone_1.default)(value);
        if (valueMoment.isValid() && !valueMoment.isSame(dateMoment, 'date')) {
            const newMoment = (0, dateTime_1.setDateToTimeMoment)(dateMoment, valueMoment);
            onChange?.(newMoment);
        }
    }, [date]);
    return (react_1.default.createElement(index_1.Box, { display: "flex", flexDirection: military ? 'column' : 'row' },
        react_1.default.createElement(index_1.ClickAwayListener, { onClickAway: picker.close },
            react_1.default.createElement(index_1.Box, { ref: field, flex: 1, mr: military ? 0 : 1.5, style: { position: 'relative' } },
                react_1.default.createElement(index_1.MaskedInput, { fullWidth: true, ...rest, InputLabelProps: { shrink: inputValue ? true : undefined }, value: inputValue, mask: military ? 'military' : 'time', placeholder: placeholder, InputProps: {
                        endAdornment: !military && (react_1.default.createElement(ArrowDropDown_1.default, { color: "action", onClick: () => {
                                picker.open();
                            } })),
                    }, onFocus: () => {
                        picker.open();
                        rest.onFocus && rest.onFocus();
                    }, onChange: (newValue) => {
                        handleChange(newValue, period);
                        setInputValue(newValue);
                    }, type: military ? 'tel' : '' }),
                picker.isOpen && !military && (react_1.default.createElement(TimePickerOptions_1.default, { ref: fixedOptions.elementRef, options: options, onChange: (value) => {
                        handleChange(value, period);
                        setInputValue(value);
                        picker.close();
                    } })))),
        getPeriodType()));
};
exports.default = TimePicker;
